<template>
  <div class="price-list-admin" v-if="currentUser">
    <div class="page-header">
      <h1>Cennik</h1>
      <div class="header-actions">
        <button
            v-if="subscriptions.length <= 10"
            @click="$router.push('/admin-profile/price-list/add')"
            class="btn-primary"
        >
          + Dodaj plan
        </button>
      </div>
    </div>

    <div class="section-card position-relative">
      <div class="section-card-header">
        <HintComponent page-key="admin_panel_price_list" :absolute="true"/>
        <h2>Lista planów subskrypcji</h2>
        <div class="meta-row">
          <span class="plans-counter">Liczba planów: <b>{{ subscriptions.length }}</b>/10</span>
          <button class="btn-outline" @click="getSubscriptions" :disabled="isSavingOrder">Odśwież</button>
        </div>
        <p v-if="subscriptions.length > 1" class="drag-hint">
          Złap kartę i upuść ją w wybranym miejscu, aby ustawić kolejność wyświetlania planów.
        </p>
      </div>

      <div v-if="subscriptions.length > 0" class="plans-grid">
        <div
            v-for="(sub, index) in subscriptions"
            :key="sub.id"
            class="plan-drag-wrapper"
            draggable="true"
            :class="{
              'plan-drag-wrapper--dragging': draggedIndex === index,
              'plan-drag-wrapper--saving': isSavingOrder
            }"
            @dragstart="onDragStart(index)"
            @dragover.prevent
            @dragenter.prevent="onDragEnter(index)"
            @drop.prevent="onDrop(index)"
            @dragend="onDragEnd"
        >
          <div class="drag-handle" title="Przeciągnij, aby zmienić kolejność">
            ⋮⋮
          </div>
          <SubPlanCard
              :id="sub.id"
              :name="sub.name"
              :subscription-m="sub.subscriptionM"
              :additional-s-m-s="sub.additionalSMS"
              :additional-user-m="sub.additionalUserM"
              :free-users="sub.freeUsers"
              :free-s-m-s="sub.freeSMS"
              :period="sub.period"
              @refreshSubscriptions="getSubscriptions"
          />
        </div>
      </div>

      <div v-else class="empty-state">
        <p>Brak planów do wyświetlenia.</p>
      </div>
    </div>
  </div>
</template>

<script>
import SubPlanCard from '@/components/SpecialComponents/SubPlanCard.vue';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import authHeader from '@/services/auth-header';
import HintComponent from "@/components/SpecialComponents/HintComponent.vue";

export default {
  name: 'SepcsEdit',
  components: {HintComponent, SubPlanCard },
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      subscriptions: [],
      draggedIndex: null,
      isSavingOrder: false,
    };
  },
  props: ['isVisible'],
  computed: {
    currentUser() {
      return this.authStore.user;
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getSubscriptions();
  },
  methods: {
    sortSubscriptionsByPosition(subscriptions) {
      return [...subscriptions].sort((a, b) => {
        const positionA = Number.isFinite(Number(a.position)) ? Number(a.position) : Number.MAX_SAFE_INTEGER;
        const positionB = Number.isFinite(Number(b.position)) ? Number(b.position) : Number.MAX_SAFE_INTEGER;

        if (positionA === positionB) {
          return Number(a.id) - Number(b.id);
        }

        return positionA - positionB;
      });
    },
    getSubscriptions() {
      axios.post('/SubscriptionSearch', {
        page: 1,
        limit: 10,
      }).then(res => {
        this.subscriptions = this.sortSubscriptionsByPosition(res.data.items || []);
      }).catch(() => {
        this.subscriptions = [];
      });
    },
    onDragStart(index) {
      if (this.isSavingOrder) return;

      this.draggedIndex = index;
    },
    onDragEnter(index) {
      if (this.isSavingOrder || this.draggedIndex === null || this.draggedIndex === index) return;

      const reorderedSubscriptions = [...this.subscriptions];
      const draggedSubscription = reorderedSubscriptions.splice(this.draggedIndex, 1)[0];

      reorderedSubscriptions.splice(index, 0, draggedSubscription);

      this.subscriptions = reorderedSubscriptions;
      this.draggedIndex = index;
    },
    onDrop() {
      if (this.isSavingOrder || this.draggedIndex === null) return;

      this.saveSubscriptionsOrder();
    },
    onDragEnd() {
      this.draggedIndex = null;
    },
    saveSubscriptionsOrder() {
      this.isSavingOrder = true;

      const orderedSubscriptions = this.subscriptions.map((subscription, index) => ({
        ...subscription,
        position: index + 1,
      }));

      this.subscriptions = orderedSubscriptions;

      Promise.all(
          orderedSubscriptions.map(subscription => axios.post('/Subscription', subscription, authHeader()))
      ).then(() => {
        this.appStore.setIsSuccess('Zapisano kolejność planów');
        this.getSubscriptions();
      }).catch(() => {
        this.appStore.setIsAlert('Nie udało się zapisać kolejności planów');
        this.getSubscriptions();
      }).finally(() => {
        this.isSavingOrder = false;
        this.draggedIndex = null;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.price-list-admin {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.plans-counter {
  font-size: 1.4rem;
  color: #374151;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 6px 12px;
}

.drag-hint {
  margin: 0;
  color: #6b7280;
  font-size: 1.35rem;
}

.plans-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
}

.plan-drag-wrapper {
  position: relative;
  cursor: grab;
  transition: opacity 0.2s, transform 0.2s;
}

.plan-drag-wrapper--dragging {
  opacity: 0.55;
  transform: scale(0.98);
}

.plan-drag-wrapper--saving {
  pointer-events: none;
  opacity: 0.75;
}

.plan-drag-wrapper:active {
  cursor: grabbing;
}

.drag-handle {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.7rem;
  line-height: 1;
  letter-spacing: -3px;
  user-select: none;
}

.plan-drag-wrapper:hover .drag-handle {
  border-color: #10b981;
  color: #10b981;
}

.empty-state {
  text-align: center;
  padding: 28px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}

.btn-primary {
  background-color: #f97316;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #ea6c0a;
}

.btn-green {
  background-color: #10b981;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-green:hover {
  background-color: #059669;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.4rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

@media (max-width: 480px) {
  .price-list-admin {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .btn-primary,
  .btn-green,
  .btn-outline {
    width: 100%;
    text-align: center;
  }

  .header-actions {
    width: 100%;
  }

  .plan-drag-wrapper {
    width: 100%;
  }

  .drag-handle {
    top: 8px;
    left: 8px;
  }
}
</style>